import { MissionAction } from '@/modules/companyNeed/views/details/missions/types';

import type { TableAction } from '@/dsComponents/Table/types';

import {
	DropdownGeneratorActionType,
	DropdownItemColors,
} from '@/dsComponents/Dropdown/types';

interface MissionActionConfig {
	iconLeft: string;
	label: string;
	onClick: () => void;
	iconLeftColor?: DropdownItemColors;
	labelColor?: DropdownItemColors;
}

interface MissionActionHandlers {
	onDetails: (missionId: string) => void;
	onPropositions: (missionId: string) => void;
	onEdit: (missionId: string) => void;
	onDuplicate: (missionId: string) => void;
	onSendToEvolia: (missionId: string) => void;
	onExtend: (missionId: string) => void;
	onValidate: (missionId: string) => void;
	onCancel: (missionId: string) => void;
	onAddWorkers: (missionId: string) => void;
}

/**
 * Formats mission actions for the Table component based on available actions.
 *
 * @param missionId - The mission ID for action callbacks
 * @param availableActions - Array of available actions from the backend
 * @param handlers - Object containing callback handlers for each action type
 * @param t - Translation function from useI18n, must be provided by the caller
 * @returns Array of TableAction configurations for the dropdown menu
 */
export const formatMissionActions = (
	missionId: string,
	availableActions: MissionAction[],
	handlers: MissionActionHandlers,
	t: (key: string) => string
): TableAction[] => {
	// Map of all possible actions with their configurations
	const actionMap: Record<
		MissionAction,
		{ type: string; item: MissionActionConfig }
	> = {
		[MissionAction.DETAILS]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'doc',
				label: t('page.companyNeed.missions.actions.details'),
				onClick: () => handlers.onDetails(missionId),
			},
		},
		[MissionAction.PROPOSITIONS]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'users',
				label: t('page.companyNeed.missions.actions.propositions'),
				onClick: () => handlers.onPropositions(missionId),
			},
		},
		[MissionAction.ADD_WORKERS]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'plus',
				label: t('page.companyNeed.missions.actions.addWorkers'),
				onClick: () => handlers.onAddWorkers(missionId),
			},
		},
		[MissionAction.EDIT]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'edit',
				label: t('global.actions.edit'),
				onClick: () => handlers.onEdit(missionId),
			},
		},
		[MissionAction.DUPLICATE]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'copy',
				label: t('global.actions.duplicate'),
				onClick: () => handlers.onDuplicate(missionId),
			},
		},
		[MissionAction.SEND_TO_EVOLIA]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'send',
				label: t('page.companyNeed.missions.actions.sendToEvolia'),
				onClick: () => handlers.onSendToEvolia(missionId),
			},
		},
		[MissionAction.EXTEND]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'calendar-plus',
				label: t('page.companyNeed.missions.actions.extend'),
				onClick: () => handlers.onExtend(missionId),
			},
		},
		[MissionAction.VALIDATE]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'circle-check',
				label: t('page.companyNeed.missions.actions.validate'),
				onClick: () => handlers.onValidate(missionId),
				iconLeftColor: DropdownItemColors.black,
				labelColor: DropdownItemColors.black,
			},
		},
		[MissionAction.CANCEL]: {
			type: DropdownGeneratorActionType.item,
			item: {
				iconLeft: 'x-circle',
				label: t('page.companyNeed.missions.actions.cancel'),
				onClick: () => handlers.onCancel(missionId),
				iconLeftColor: DropdownItemColors.red,
				labelColor: DropdownItemColors.red,
			},
		},
	};

	// biome-ignore lint/suspicious/noExplicitAny: Biome Init
	const items: any[] = [];
	const primaryActions = [
		MissionAction.DETAILS,
		MissionAction.PROPOSITIONS,
		MissionAction.ADD_WORKERS,
	];
	const secondaryActions = [
		MissionAction.EDIT,
		MissionAction.DUPLICATE,
		MissionAction.SEND_TO_EVOLIA,
		MissionAction.EXTEND,
		MissionAction.VALIDATE,
	];
	const dangerActions = [MissionAction.CANCEL];

	primaryActions.forEach((action) => {
		if (availableActions.includes(action)) {
			items.push(actionMap[action]);
		}
	});

	const hasSecondaryActions = secondaryActions.some((action) =>
		availableActions.includes(action)
	);
	if (items.length > 0 && hasSecondaryActions) {
		items.push({ type: DropdownGeneratorActionType.separator });
	}

	secondaryActions.forEach((action) => {
		if (availableActions.includes(action)) {
			items.push(actionMap[action]);
		}
	});

	const hasDangerActions = dangerActions.some((action) =>
		availableActions.includes(action)
	);
	if (items.length > 0 && hasDangerActions) {
		items.push({ type: DropdownGeneratorActionType.separator });
	}

	dangerActions.forEach((action) => {
		if (availableActions.includes(action)) {
			items.push(actionMap[action]);
		}
	});

	return [
		{
			button: { iconOnly: 'dots', type: 'neutral', variant: 'ghost' },
			dropdownItems: () => items,
			width: '240px',
		},
	];
};
